import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

/**
 * Loads a GLB/GLTF model from a URL and renders a PNG thumbnail.
 * Returns a data URL (base64 PNG) that can be uploaded or displayed directly.
 */
export default async function renderGlbThumbnail(glbUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = 300
    canvas.height = 300

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 1000)
    camera.position.set(1, 1, 1)

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
    })
    renderer.setSize(300, 300, false)
    renderer.outputColorSpace = THREE.SRGBColorSpace

    scene.add(new THREE.AmbientLight(0xffffff, 0.9))
    const dir = new THREE.DirectionalLight(0xffffff, 0.8)
    dir.position.set(2, 3, 4)
    scene.add(dir)

    const loader = new GLTFLoader()
    const dracoLoader = new DRACOLoader()
      .setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
      .setDecoderConfig({ type: 'wasm' })
    loader.setDRACOLoader(dracoLoader)

    const dispose = () => {
      renderer.dispose()
      scene.traverse((o: any) => {
        if (o.isMesh) {
          o.geometry?.dispose?.()
          if (o.material) {
            const mats = Array.isArray(o.material) ? o.material : [o.material]
            mats.forEach((m: any) => {
              for (const k in m) m[k]?.isTexture && m[k].dispose?.()
              m.dispose?.()
            })
          }
        }
      })
      dracoLoader.dispose()
    }

    loader.load(
      glbUrl,
      (gltf) => {
        try {
          const root = gltf.scene
          scene.add(root)

          const box = new THREE.Box3().setFromObject(root)
          const size = box.getSize(new THREE.Vector3())
          const center = box.getCenter(new THREE.Vector3())
          root.position.sub(center)

          const maxDim = Math.max(size.x, size.y, size.z) || 1
          const tight = 0.7
          const dist = maxDim / (2 * Math.tan((Math.PI * camera.fov) / 360))
          camera.position.set(dist * tight, dist * tight, dist * tight)
          camera.lookAt(0, 0, 0)

          renderer.render(scene, camera)
          const dataUrl = canvas.toDataURL('image/png')
          dispose()
          resolve(dataUrl)
        } catch (err) {
          dispose()
          reject(err)
        }
      },
      undefined,
      (err) => {
        dispose()
        reject(err)
      },
    )
  })
}
